// Maps a tree node onto a fit segment so the tree carries the same colour
// language as the model performance chart, instead of a standalone red/blue
// gradient that means nothing outside this one page.
//
// Deliberate simplification, decided rather than assumed. Thresholds cut the
// ENSEMBLED score, which is the sum of each tree's node conversion rate times
// that tree's weight. A node on its own has a conversion rate, not a score, so
// on a multi-tree model this states a segment the node does not strictly have.
// It is exact for a single tree at weight 1 and directionally right elsewhere,
// which is the trade chosen for a consistent reading across models.
import { CHART_COLORS } from '../../../utils';
import { EnsembleThreshold } from '../../../models/ensembling';
import { Threshold } from '../../../models/ModelPerformances';

// Highest first: the first threshold a rate clears is the segment it lands in.
const RANKED: Exclude<Threshold, 'low'>[] = ['very good', 'good', 'medium'];

/**
 * The Customer Fit segment names as they are written everywhere else in the
 * product. Lives here, next to the segment itself, because three surfaces used
 * to each keep their own copy and the node cards had drifted to inventing
 * "High fit" and "Average" for the same two bands.
 */
export const SEGMENT_LABEL: Record<Threshold, string> = {
  'very good': 'Very Good',
  good: 'Good',
  medium: 'Medium',
  low: 'Low',
};

/**
 * Fit segment for a node conversion rate (0 to 1), or null when the model has no
 * thresholds set yet, in which case callers keep whatever colour they had.
 */
export function nodeSegment(
  conversionRate: number,
  thresholds?: EnsembleThreshold
): Threshold | null {
  if (!thresholds) return null;
  // An unconfigured model reads as all-zero, where every node would come out
  // "very good". Treat that as "no thresholds" rather than colouring on it.
  const configured = RANKED.some((name) => Number(thresholds[name]) > 0);
  if (!configured || !Number.isFinite(conversionRate)) return null;

  // Thresholds are set on the score scale, which is the conversion rate as a
  // percentage (0 to 100), so the node rate is scaled to match.
  const score = conversionRate * 100;
  const hit = RANKED.find((name) => score >= Number(thresholds[name]));
  return hit ?? 'low';
}

/** The colour the performance bar chart uses for that segment. */
export function segmentColor(segment: Threshold | null): string | null {
  if (!segment) return null;
  return CHART_COLORS.customer_fit[segment]?.background ?? null;
}

// Text that sits ON a segment fill. Measured contrast against each background,
// because the palette's own `text` field claims white for all four and white
// only works on two of them:
//
//   very good #1e77cc   white 4.6   dark 3.5
//   good      #46C5B5   white 2.1   dark 7.7
//   medium    #EEC240   white 1.7   dark 9.6
//   low       #d11f4d   white 5.2   dark 3.1
//
// The teal and the yellow are light enough that white on them is unreadable at
// card size, which defeats the point of filling the card in the first place.
const ON_FILL: Record<Threshold, string> = {
  'very good': '#fff',
  good: '#0f3b35',
  medium: '#4a3a08',
  low: '#fff',
};

/**
 * The colour text must be to sit on `segmentColor(segment)`. Null when there is
 * no segment, in which case nothing is filled and the caller keeps its own text.
 */
export function segmentTextColor(segment: Threshold | null): string | null {
  if (!segment) return null;
  return ON_FILL[segment] ?? null;
}

export interface SegmentBand {
  segment: Threshold;
  from: number;
  to: number;
}

/**
 * The gains curve walks nodes from the highest conversion rate down, so its
 * x-axis passes through the fit segments in order. This returns the x ranges
 * (as % of population) each segment occupies, which is what lets the chart show
 * where "very good" ends and "good" begins rather than implying it.
 *
 * Empty when the model has no thresholds set, so callers draw no bands at all
 * rather than bands that mean nothing.
 */
export function segmentBands(
  precisions: {
    conversionRate: number;
    percentTotalPopulation: number;
  }[],
  thresholds?: EnsembleThreshold
): SegmentBand[] {
  const bands: SegmentBand[] = [];
  let from = 0;

  precisions.forEach((precision) => {
    const segment = nodeSegment(precision.conversionRate, thresholds);
    const to = precision.percentTotalPopulation;
    if (segment) {
      const last = bands[bands.length - 1];
      // Consecutive nodes in the same segment are one band, not one per node.
      if (last && last.segment === segment) {
        last.to = to;
      } else {
        bands.push({ segment, from, to });
      }
    }
    from = to;
  });

  return bands;
}
